import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { useToast } from "@/components/Toast";
import type { RealKey } from "@/types";

const SDK_LANGS = ["python-anthropic", "python-openai", "python-httpx", "node-openai", "curl"] as const;
type SdkLang = typeof SDK_LANGS[number];

const SDK_LABELS: Record<SdkLang, string> = {
  "python-anthropic": "Python — Anthropic SDK",
  "python-openai": "Python — OpenAI SDK",
  "python-httpx": "Python — generic httpx",
  "node-openai": "Node.js — OpenAI SDK",
  curl: "curl / shell",
};

// Derive the gateway URL from the page origin so when this admin UI is
// served from a custom Cloudflare-fronted domain (e.g. gate.smarttouch.solutions)
// the snippet sends consumers through THAT domain instead of pinning them
// to the Railway-internal URL — which would bypass any Cloudflare WAF/DDoS
// protection and silently break if/when Railway rotates the edge target.
// (Audit finding #12.)
const GATEWAY_URL =
  typeof window !== "undefined" && window.location?.origin
    ? `${window.location.origin}/p`
    : "https://gatekeeper-production-2a41.up.railway.app/p";

export function Onboard() {
  const [reals, setReals] = useState<RealKey[]>([]);
  const [realKeyId, setRealKeyId] = useState<number | "">("");
  const [proxyLabel, setProxyLabel] = useState("");
  const [project, setProject] = useState("");
  const [language, setLanguage] = useState<SdkLang>("python-anthropic");
  const [serviceName, setServiceName] = useState("backend");
  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    api<RealKey[]>("/api/admin/real-keys")
      .then(setReals)
      .catch((e) => setErr(e?.message ?? "load failed"));
  }, []);

  // Wipe the just-minted token from React state on unmount AND after a
  // 2-minute display window. Stops a casual "navigate away then come back"
  // from re-revealing the secret if the SPA preserves component state.
  // (Audit finding #14.)
  useEffect(() => {
    if (!issuedKey) return;
    const t = window.setTimeout(() => setIssuedKey(null), 120_000);
    return () => {
      window.clearTimeout(t);
      setIssuedKey(null);
    };
  }, [issuedKey]);

  // Audit 2026-05-21 P2: visible countdown so operators understand why the
  // secret panel will vanish. Previously the auto-clear surprised users into
  // re-minting (token sprawl). 1-second tick is cheap; clears when issuedKey
  // does.
  const [tokenAgeMs, setTokenAgeMs] = useState(0);
  useEffect(() => {
    if (!issuedKey) {
      setTokenAgeMs(0);
      return;
    }
    const start = Date.now();
    const t = window.setInterval(() => setTokenAgeMs(Date.now() - start), 1000);
    return () => window.clearInterval(t);
  }, [issuedKey]);
  const tokenCountdownLabel = (() => {
    if (!issuedKey) return "";
    const remaining = Math.max(0, 120_000 - tokenAgeMs);
    const s = Math.floor(remaining / 1000);
    return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
  })();

  const real = reals.find((r) => r.id === realKeyId);

  async function mint() {
    if (!real || !proxyLabel.trim()) {
      setErr("Pick a real key and give the proxy a label first.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const expires = new Date();
      expires.setUTCFullYear(expires.getUTCFullYear() + 1);
      const r = await api<{ full_value: string }>("/api/admin/proxy-keys", {
        method: "POST",
        json: {
          real_key_id: real.id,
          label: proxyLabel.trim(),
          project: project.trim() || null,
          expires_at: expires.toISOString(),
        },
      });
      setIssuedKey(r.full_value);
      toast.success("Proxy key minted — copy snippet below");
    } catch (e: any) {
      setErr(e?.message ?? "mint failed");
    } finally {
      setBusy(false);
    }
  }

  const codeSnippet = issuedKey ? buildSnippet(language, issuedKey, real?.upstream_base_url) : "";
  const railwayCmds = issuedKey ? buildRailwayCmds(serviceName, issuedKey) : "";

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-4xl display tracking-tight">Connect a new consumer</h1>
        <p className="text-sm text-slate-400 mt-1">
          Pick a real key, mint a proxy, get a copy-paste snippet for the consumer's Railway env
          and code.
        </p>
      </header>

      <div className="card space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Real key (upstream)">
            <select
              className="input"
              value={realKeyId}
              onChange={(e) => setRealKeyId(Number(e.target.value) || "")}
            >
              <option value="">— pick one —</option>
              {reals.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label} ({r.provider})
                </option>
              ))}
            </select>
          </Field>
          <Field label="New proxy key label">
            <input
              className="input mono"
              value={proxyLabel}
              onChange={(e) => setProxyLabel(e.target.value)}
              placeholder="myapp-prod"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Project (groups proxy keys on the dashboard)">
            <input
              className="input"
              value={project}
              onChange={(e) => setProject(e.target.value)}
              placeholder="myapp"
            />
          </Field>
          <Field label="Consumer's Railway service name">
            <input
              className="input mono"
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
              placeholder="backend"
            />
          </Field>
        </div>
        <Field label="Code language / SDK on the consumer side">
          <select
            className="input"
            value={language}
            onChange={(e) => setLanguage(e.target.value as SdkLang)}
          >
            {SDK_LANGS.map((l) => (
              <option key={l} value={l}>
                {SDK_LABELS[l]}
              </option>
            ))}
          </select>
        </Field>

        {err && <div className="text-rose-300 text-sm">{err}</div>}

        {!issuedKey && (
          <div className="flex justify-end">
            <button
              className="btn-primary"
              disabled={busy || !realKeyId || !proxyLabel.trim()}
              onClick={mint}
            >
              {busy ? "Minting…" : "Mint proxy key"}
            </button>
          </div>
        )}
      </div>

      {issuedKey && (
        <div className="space-y-4">
          <div className="card space-y-3 border border-amber-500/30 bg-amber-500/5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-amber-200">⚠️ One-time view</h2>
              <div className="flex items-center gap-2">
                <span
                  className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-200 mono"
                  title="The token clears from view at 2:00 to prevent accidental re-reveal."
                >
                  clears in {tokenCountdownLabel}
                </span>
                <button
                  className="text-xs px-2 py-1 rounded border border-white/10 hover:bg-white/5"
                  onClick={() => navigator.clipboard.writeText(issuedKey)}
                >
                  copy token
                </button>
              </div>
            </div>
            <pre className="mono text-xs text-amber-100 break-all bg-black/30 p-3 rounded">
              {issuedKey}
            </pre>
            <p className="text-xs text-slate-400">
              This token is shown once. Save it to 1Password now — Gatekeeper only stores its hash.
              Token panel will auto-clear in 2 minutes to prevent accidental re-reveal.
            </p>
          </div>

          <div className="card space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Railway env vars</h2>
              <button
                className="text-xs px-2 py-1 rounded border border-white/10 hover:bg-white/5"
                onClick={() => navigator.clipboard.writeText(railwayCmds)}
              >
                copy
              </button>
            </div>
            <pre className="mono text-xs text-slate-200 bg-black/30 p-3 rounded overflow-x-auto whitespace-pre">
              {railwayCmds}
            </pre>
          </div>

          <div className="card space-y-2">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Code change ({SDK_LABELS[language]})</h2>
              <button
                className="text-xs px-2 py-1 rounded border border-white/10 hover:bg-white/5"
                onClick={() => navigator.clipboard.writeText(codeSnippet)}
              >
                copy
              </button>
            </div>
            <pre className="mono text-xs text-slate-200 bg-black/30 p-3 rounded overflow-x-auto whitespace-pre">
              {codeSnippet}
            </pre>
          </div>

          <div className="text-sm text-slate-500">
            <Link to="/proxy-keys" className="text-accent-500 hover:underline">
              View proxy key
            </Link>
            {" "}— smoke-test it from there before pushing the consumer change.
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs text-slate-400 mb-1">{label}</div>
      {children}
    </label>
  );
}

function buildRailwayCmds(service: string, token: string): string {
  return `railway variables --service ${service} \\
  --set "GATEKEEPER_GATEWAY=${GATEWAY_URL}" \\
  --set "GATEKEEPER_PROXY_KEY=${token}"`;
}

function buildSnippet(lang: SdkLang, token: string, upstream?: string): string {
  const _ = upstream;
  const prefixComment = "# Routes through Gatekeeper so the upstream key can be rotated\n# without redeploying — pattern from VerdictExp.";
  switch (lang) {
    case "python-anthropic":
      return `${prefixComment}
import os
from anthropic import AsyncAnthropic

GATEWAY = os.getenv("GATEKEEPER_GATEWAY", "").rstrip("/")
PROXY_KEY = os.getenv("GATEKEEPER_PROXY_KEY", "")

if GATEWAY and PROXY_KEY:
    client = AsyncAnthropic(
        api_key="gatekeeper-routed",  # SDK requires non-empty; gateway strips it
        base_url=GATEWAY,
        default_headers={"X-Vault-Key": PROXY_KEY},
    )
else:
    client = AsyncAnthropic(api_key=os.getenv("ANTHROPIC_API_KEY", ""))`;
    case "python-openai":
      return `${prefixComment}
import os
from openai import AsyncOpenAI

GATEWAY = os.getenv("GATEKEEPER_GATEWAY", "").rstrip("/")
PROXY_KEY = os.getenv("GATEKEEPER_PROXY_KEY", "")

if GATEWAY and PROXY_KEY:
    client = AsyncOpenAI(
        api_key="gatekeeper-routed",
        base_url=GATEWAY,
        default_headers={"X-Vault-Key": PROXY_KEY},
    )
else:
    client = AsyncOpenAI(api_key=os.getenv("OPENAI_API_KEY", ""))`;
    case "python-httpx":
      return `# Generic httpx pattern — works for any upstream Gatekeeper supports.
import os, httpx

GATEWAY = os.getenv("GATEKEEPER_GATEWAY", "").rstrip("/")
PROXY_KEY = os.getenv("GATEKEEPER_PROXY_KEY", "")

async def upstream_get(path: str, **kwargs):
    headers = kwargs.pop("headers", {})
    headers["X-Vault-Key"] = PROXY_KEY
    async with httpx.AsyncClient(base_url=GATEWAY, timeout=30.0) as c:
        return await c.get(path, headers=headers, **kwargs)`;
    case "node-openai":
      return `${prefixComment}
import OpenAI from "openai";

const GATEWAY = (process.env.GATEKEEPER_GATEWAY ?? "").replace(/\\/$/, "");
const PROXY_KEY = process.env.GATEKEEPER_PROXY_KEY ?? "";

const openai = GATEWAY && PROXY_KEY
  ? new OpenAI({
      apiKey: "gatekeeper-routed",
      baseURL: GATEWAY,
      defaultHeaders: { "X-Vault-Key": PROXY_KEY },
    })
  : new OpenAI({ apiKey: process.env.OPENAI_API_KEY });`;
    case "curl":
    default:
      return `# curl through the gateway — no SDK needed
curl -X POST "$GATEKEEPER_GATEWAY/v1/messages" \\
  -H "X-Vault-Key: $GATEKEEPER_PROXY_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"claude-haiku-4-5-20251001","max_tokens":12,"messages":[{"role":"user","content":"hi"}]}'`;
  }
}
